import { useState } from 'react'
import { FolderField } from '../servers/FolderField'
import { ApiError } from '../transport/http'
import { agentClone, CLONE_TYPES, type AgentRow, type CloneType } from '../transport/api'

/**
 * CLONE INTO A CHOSEN TYPE — owner, 2026-09-30: Grok clone 就變成 Grok … 或者不同 Agent 之間
 * 的 clone 可以互換 Type; 「我們先做能做的, cursor 之後再補」.
 *
 * Any agent (all five CLIs) → a new Claude, Codex or Grok agent on this machine that
 * remembers the conversation. Claude → Claude and Codex → Codex use the CLI's own fork
 * (everything carries); any other pair carries the messages, tools as text (edge
 * agentClone.ts). The source keeps running.
 */
const LABEL: Record<CloneType, string> = { claude: 'Claude', codex: 'Codex', grok: 'Grok' }

export function CloneForm({
  agent,
  product,
  deviceId,
  machine,
  onCloned,
}: {
  agent: AgentRow
  product: string | null
  deviceId: string
  machine: string
  onCloned: (newName: string) => void
}) {
  const own = CLONE_TYPES.find((t) => LABEL[t] === product) ?? null
  const [type, setType] = useState<CloneType>(own ?? 'claude')
  const [name, setName] = useState(`${agent.session}-clone`)
  const [cwd, setCwd] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const native = own === type && (type === 'claude' || type === 'codex')

  const submit = async () => {
    const nm = name.trim()
    const dir = cwd.trim()
    if (!nm || !dir) return
    setBusy(true)
    setErr(null)
    try {
      const r = await agentClone(deviceId, { session: agent.session, target: type, new_name: nm, cwd: dir })
      if (r.error) {
        setErr(r.message ?? r.error)
        return
      }
      onCloned(r.session ?? nm)
    } catch (e) {
      const body = (e instanceof ApiError ? e.body : null) as { error?: string; message?: string } | null
      setErr(
        body?.error === 'unknown_route'
          ? `${machine} needs an update before it can clone.`
          : (body?.message ?? body?.error ?? String(e instanceof Error ? e.message : e)),
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="sm-fork">
      <label className="sm-field">
        <span className="sm-label">Type</span>
        <select
          className="sm-input"
          aria-label="Type"
          value={type}
          disabled={busy}
          onChange={(e) => setType(e.target.value as CloneType)}
        >
          {CLONE_TYPES.map((t) => (
            <option key={t} value={t}>
              {LABEL[t]}
            </option>
          ))}
        </select>
      </label>

      <label className="sm-field">
        <span className="sm-label">New agent name</span>
        <input
          className="sm-input"
          value={name}
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
          disabled={busy}
          onChange={(e) => setName(e.target.value)}
        />
      </label>

      <div className="sm-field">
        <span className="sm-label">Folder path</span>
        <FolderField deviceId={deviceId} serverName={machine} value={cwd} onChange={(p) => setCwd(p)} />
      </div>

      <p className="sm-hint">
        {native
          ? `${LABEL[type]}'s own fork — everything carries over.`
          : `The conversation carries over to ${LABEL[type]}; tool steps come as text.`}
      </p>

      {err && <p className="err-line">{err}</p>}

      <div className="sm-actions">
        <button
          type="button"
          className="sheet-btn primary"
          disabled={busy || !name.trim() || !cwd.trim()}
          onClick={() => void submit()}
        >
          {busy ? 'Cloning…' : 'Clone'}
        </button>
      </div>
    </div>
  )
}
